import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { qk } from '@/api/queries';
import { AI_ROUTES, type AiRoute, type AiValues, type SecretState, type Section } from '@/api/types';
import { Button, Drawer, FieldHint, Input, Segmented, useToast } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { patchSection } from './api';
import { SecretField } from './SecretField';
import s from './settings.module.css';

type Provider = 'anthropic' | 'openai';

/** i18n keys cannot hold a dot (`structure.suggest`). */
export const routeKey = (route: AiRoute) => route.replace('.', '_');

/** The routes Settings → AI sends to OpenAI (P8-A03). */
export function openAiRoutes(v: AiValues): AiRoute[] {
  return AI_ROUTES.filter((r) => v.route_providers?.[r] === 'openai');
}

/**
 * The second provider (P8-A03, additive: the design's dashed «Дополнительный провайдер» card opens
 * it): OpenAI behind the same LLM port, its model, a write-only API key, and which model routes it
 * answers. A route moves to OpenAI only on eval results (evals/providers.py); the rest stay on Claude.
 */
export function OpenAiDrawer({ data, onClose }: { data: Section<AiValues>; onClose: () => void }) {
  const { t } = useTranslation('settings');
  const tv = t as unknown as (k: string) => string;
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const v = data.values;
  const [model, setModel] = useState(v.openai_model ?? 'gpt-5');
  const [routes, setRoutes] = useState<Record<AiRoute, Provider>>(
    () => Object.fromEntries(AI_ROUTES.map((r) => [r, v.route_providers?.[r] === 'openai' ? 'openai' : 'anthropic'])) as Record<AiRoute, Provider>,
  );
  const [typed, setTyped] = useState('');
  const [clearing, setClearing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const secret = (data.secrets as Partial<Record<'openai_api_key', SecretState>>).openai_api_key;

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (model.trim() !== (v.openai_model ?? 'gpt-5')) body.openai_model = model.trim();
      const next = Object.fromEntries(AI_ROUTES.filter((r) => routes[r] === 'openai').map((r) => [r, 'openai']));
      const before = Object.fromEntries(openAiRoutes(v).map((r) => [r, 'openai']));
      if (JSON.stringify(next) !== JSON.stringify(before)) body.route_providers = next;
      if (typed.trim()) body.openai_api_key = typed.trim();
      else if (clearing) body.openai_api_key = null;
      return patchSection<AiValues>('ai', body);
    },
    onSuccess: (next) => {
      // The response carries only {set, masked}: the typed key is dropped from memory with the form.
      setTyped('');
      qc.setQueryData(qk.settings('ai'), next);
      toast(t('openai_edit.saved'));
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    save.mutate();
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('openai_edit.title')}
      sub={t('openai_edit.sub')}
      closeLabel={tc('actions.close_esc')}
      footerNote={
        problem && (
          <span className={s.problem} role="alert">
            {problem}
          </span>
        )
      }
      footer={
        <div className={s.drawerActions}>
          <Button size={38} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={38} type="submit" form="openai-settings" disabled={save.isPending}>
            {t('ai_edit.save')}
          </Button>
        </div>
      }
    >
      <form id="openai-settings" className={s.drawerForm} onSubmit={submit} noValidate>
        <Input label={t('openai_edit.model')} value={model} onChange={(e) => setModel(e.target.value)} mono />
        <SecretField
          testId="openai-key-state"
          labels={{
            label: t('ai_edit.key'),
            set: (masked) => t('ai_edit.key_set', { masked }),
            unset: t('ai_edit.key_unset'),
            replace: t('ai_edit.key_new'),
            clear: t('ai_edit.key_clear'),
            willClear: t('ai_edit.key_will_clear'),
            hint: t('ai_edit.key_hint'),
          }}
          state={secret}
          value={typed}
          clearing={clearing}
          onType={(value) => {
            setTyped(value);
            setClearing(false);
          }}
          onClear={() => setClearing((x) => !x)}
        />
        <div className={s.group}>
          <span className={s.groupLabel}>{t('openai_edit.routes')}</span>
          {AI_ROUTES.map((r) => (
            <div key={r} className={s.routeRow}>
              <span className={s.routeName}>{tv(`ai.route_names.${routeKey(r)}`)}</span>
              <Segmented
                variant="form"
                label={tv(`ai.route_names.${routeKey(r)}`)}
                value={routes[r]}
                onChange={(p: Provider) => setRoutes((x) => ({ ...x, [r]: p }))}
                options={[
                  { value: 'anthropic', label: t('openai_edit.claude') },
                  { value: 'openai', label: t('ai.providers.openai') },
                ]}
              />
            </div>
          ))}
          <FieldHint>{t('openai_edit.routes_hint')}</FieldHint>
        </div>
      </form>
    </Drawer>
  );
}
